/**
 * Catalog display mappings (ADR-0103). Pure functions that map the
 * `CatalogProduct` wire shape into display strings / pill tones for the
 * Catalog view. Money is formatted from minor units via the shared
 * `formatMinor` — never math'd into floats here.
 */

import type {
  CatalogAvailability,
  CatalogProduct,
  CatalogProductKind,
  CatalogRecurring,
  CatalogSource,
  CatalogSyncState,
} from '@sill/types';

import { formatMinor, formatRelativeTime } from '../../lib/format.js';
import type { PillTone } from '../../components/atoms/index.js';

/**
 * Format a product's price for display. `price_minor` is an integer in
 * minor units (cents); `currency` is ISO-4217. Returns an em-dash when
 * the source did not report a price. Delegates to the shared
 * `formatMinor` so the value matches the rest of the dashboard.
 */
export function formatProductPrice(p: CatalogProduct): string {
  if (p.price_minor === undefined) return '—';
  return formatMinor(p.price_minor, p.currency ?? 'USD');
}

/**
 * Format a product's SALE price for display. Mirrors {@link formatProductPrice}
 * but reads `sale_price_minor`; an em-dash when the source did not report a
 * sale price. (The sale window — `sale_price_start`/`end` — is not surfaced in
 * the table cell; this is the price only.)
 */
export function formatSalePrice(p: CatalogProduct): string {
  if (p.sale_price_minor === undefined) return '—';
  return formatMinor(p.sale_price_minor, p.currency ?? 'USD');
}

/**
 * Format a product's `inventory_quantity` for the table cell. Renders the
 * integer count (locale-grouped) when present; an em-dash when the source did
 * not report inventory. A reported `0` is a real value (out of stock) and
 * renders as `0`, NOT an em-dash.
 */
export function formatInventory(p: CatalogProduct): string {
  if (p.inventory_quantity === undefined) return '—';
  return p.inventory_quantity.toLocaleString('en-US');
}

/**
 * Per-row "last synced" for the catalog table. Unlike the site-level
 * {@link formatLastSynced} (which takes a nullable site aggregate), every row
 * carries a non-null `last_synced_at`, so this delegates straight to the
 * shared relative-time formatter ("3h ago", "2d ago", …).
 */
export function formatProductLastSynced(
  p: CatalogProduct,
  now: number = Date.now(),
): string {
  return formatRelativeTime(p.last_synced_at, now);
}

/**
 * Compact recurring-cadence suffix for a subscription, e.g. `/mo`, `/yr`,
 * `/wk`, `/day`. When `interval_count > 1` the form widens to "every N
 * months" style so a quarterly/biennial plan is not mis-read as monthly.
 */
export function formatRecurringCadence(r: CatalogRecurring): string {
  const count = r.interval_count ?? 1;
  if (count > 1) {
    const plural: Record<CatalogRecurring['interval'], string> = {
      day: 'days',
      week: 'weeks',
      month: 'months',
      year: 'years',
    };
    return `every ${count} ${plural[r.interval]}`;
  }
  const suffix: Record<CatalogRecurring['interval'], string> = {
    day: '/day',
    week: '/wk',
    month: '/mo',
    year: '/yr',
  };
  return suffix[r.interval];
}

/** Plain-language label for a non-subscription product kind. */
function plainKindLabel(kind: CatalogProductKind): string {
  switch (kind) {
    case 'physical':
      return 'Physical';
    case 'digital':
      return 'Digital';
    case 'service':
      return 'Service';
    case 'subscription':
      // Handled by formatProductKind (it carries the cadence); this is the
      // bare fallback when recurring data is absent.
      return 'Subscription';
  }
}

/**
 * Format a product's KIND for the table cell. Non-subscription kinds read as
 * a single plain word ("Physical" / "Digital" / "Service"). A subscription
 * shows the cadence + per-interval price when available, e.g.
 * "Subscription · $9.99/mo" or "Subscription · $120.00 every 3 months";
 * with no recurring data it degrades to the bare "Subscription".
 */
export function formatProductKind(p: CatalogProduct): string {
  if (p.product_kind !== 'subscription') return plainKindLabel(p.product_kind);
  if (p.recurring === undefined) return 'Subscription';
  const cadence = formatRecurringCadence(p.recurring);
  if (p.price_minor === undefined) return `Subscription · ${cadence}`;
  const price = formatMinor(p.price_minor, p.currency ?? 'USD');
  // For the per-interval ("/mo") form, append the suffix directly to the
  // price ("$9.99/mo"); for the "every N months" form, separate with a space.
  return cadence.startsWith('/')
    ? `Subscription · ${price}${cadence}`
    : `Subscription · ${price} ${cadence}`;
}

/**
 * Return a product URL ONLY when it is a well-formed http(s) URL, else
 * `undefined`. The catalog `url` is MERCHANT-CONTROLLED (a Shopify
 * `onlineStoreUrl`, a CSV `link` cell, a Woo permalink), so it must never be
 * rendered as an `<a href>` without a scheme check — a `javascript:` / `data:`
 * value would be a (self-)XSS vector in the dashboard. Parsed with the URL API;
 * only `http:` / `https:` pass.
 */
export function safeStoreUrl(url: string | undefined): string | undefined {
  if (url === undefined) return undefined;
  try {
    const parsed = new URL(url);
    // Return the parser-NORMALIZED href (not the raw input) so the value used
    // for both the href and the tooltip is canonical — leading whitespace and
    // other noise the parser strips never reach the DOM.
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? parsed.href
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Whether a product's fields are EDITABLE in the dashboard. Only Sill-OWNED
 * sources (CSV upload + site-scan) are editable — a cart-synced product
 * (Shopify/Woo/Stripe/…) is the connector's system of record and would be
 * overwritten on the next sync, so it is "edit in {source}" instead. Mirrors
 * the server's `OWNED_CATALOG_SOURCES` guard.
 */
export function isEditableSource(source: CatalogSource): boolean {
  return source === 'csv' || source === 'site_scan';
}

/** Human-readable source label. */
export function formatSource(source: CatalogSource): string {
  switch (source) {
    case 'shopify':
      return 'Shopify';
    case 'woocommerce':
      return 'WooCommerce';
    case 'stripe':
      return 'Stripe';
    case 'thrivecart':
      return 'ThriveCart';
    case 'magento':
      return 'Magento';
    case 'csv':
      return 'CSV';
    case 'site_scan':
      return 'Site scan';
  }
}

/** Human-readable availability label. */
export function formatAvailability(a: CatalogAvailability | undefined): string {
  switch (a) {
    case 'in_stock':
      return 'In stock';
    case 'out_of_stock':
      return 'Out of stock';
    case 'pre_order':
      return 'Pre-order';
    case 'backorder':
      return 'Backorder';
    case 'unknown':
      return 'Unknown';
    case undefined:
      return '—';
  }
}

/** Pill tone for availability — mint for in-stock, coral for out, neutral else. */
export function availabilityTone(a: CatalogAvailability | undefined): PillTone {
  if (a === 'in_stock') return 'mint';
  if (a === 'out_of_stock') return 'coral';
  return 'neutral';
}

/** Eligibility badge: lime/mint when eligible, muted-neutral when not. */
export function eligibilityTone(eligible: boolean): PillTone {
  return eligible ? 'mint' : 'neutral';
}

export function eligibilityLabel(prefix: string, eligible: boolean): string {
  return `${prefix} ${eligible ? 'ELIGIBLE' : 'OFF'}`;
}

/** Sync-state label + tone. `synced` reads affirmative; `stale`/`removed` muted. */
export function syncStateLabel(s: CatalogSyncState): string {
  switch (s) {
    case 'synced':
      return 'Synced';
    case 'stale':
      return 'Stale';
    case 'removed':
      return 'Removed';
  }
}

export function syncStateTone(s: CatalogSyncState): PillTone {
  switch (s) {
    case 'synced':
      return 'mint';
    case 'stale':
      return 'amber';
    case 'removed':
      return 'neutral';
  }
}

/**
 * Site-level "last synced" caption for the data-source strip. `null` (a site
 * that has never synced) reads "never"; otherwise the shared relative-time
 * formatter ("3h ago", "2d ago", …).
 */
export function formatLastSynced(
  last_synced_at: string | null,
  now: number = Date.now(),
): string {
  if (last_synced_at === null) return 'never';
  return formatRelativeTime(last_synced_at, now);
}

/**
 * Derive a one-line, human reason for a NOT-search-eligible product, for the
 * eligibility-pill tooltip. Client-side only (ADR-0103 review): the table row
 * already carries availability; whether the site has any catalog eligibility
 * rules configured is passed in from the eligibility section.
 *
 *   - out of stock           → "Not discoverable: reported out of stock"
 *   - no rules configured    → "Eligibility not activated yet"
 *   - otherwise              → "Not matched by the current eligibility rules"
 */
export function deriveSearchIneligibleReason(
  availability: CatalogAvailability | undefined,
  has_catalog_rules: boolean,
): string {
  if (availability === 'out_of_stock') {
    return 'Not discoverable: reported out of stock';
  }
  if (!has_catalog_rules) {
    return 'Eligibility not activated yet';
  }
  return 'Not matched by the current eligibility rules';
}

/**
 * CSV import template (ADR-0103 data-source strip). The header row carries
 * EVERY column the importer recognises (`import-csv-catalog.ts` HEADER_ALIASES),
 * so a merchant sees the full supported surface — identity, commerce, and the
 * ADR-0104 non-physical/recurring fields — in one correctly-shaped starting
 * point. Sample rows cover a physical good, a one-time digital good, and a
 * recurring membership (which is search-discoverable but NOT checkout-eligible
 * — Sill cannot bill recurring yet). Only `title` is required; blank cells are
 * fine. Downloaded client-side. Kept next to the other catalog constants.
 *
 * Column reference (alias the importer also accepts in parentheses):
 *   item_id (id/sku) · title (name) · description · link (url) ·
 *   image_link (image_url) · brand (vendor) · gtin (barcode/upc/ean) ·
 *   mpn (part_number) · condition · item_group_id (group_id) · price ·
 *   currency · availability · inventory_quantity (quantity/qty) ·
 *   product_kind (type: physical|digital|subscription|service) ·
 *   fulfillment_type (shipped|digital_download|access_grant|service) ·
 *   billing_interval (day|week|month|year — subscriptions only) ·
 *   billing_interval_count · trial_days
 */
export const CATALOG_TEMPLATE_CSV: string = [
  'item_id,title,description,link,image_link,brand,gtin,mpn,condition,item_group_id,price,currency,availability,inventory_quantity,product_kind,fulfillment_type,billing_interval,billing_interval_count,trial_days',
  'SKU-001,Felt Hat,Wool felt hat,https://example.com/felt-hat,https://example.com/felt-hat.jpg,Acme,0001234567890,ACME-FH-01,new,HAT-001,25.00,USD,in_stock,42,physical,shipped,,,',
  'SKU-002,Pro Course,Lifetime access video course,https://example.com/course,https://example.com/course.jpg,Acme,,ACME-CRS-01,new,,99.00,USD,in_stock,,digital,digital_download,,,',
  'SKU-003,Membership,Monthly members area,https://example.com/membership,https://example.com/membership.jpg,Acme,,ACME-MEM-01,new,,15.00,USD,in_stock,,subscription,access_grant,month,1,14',
].join('\n');
